import { BlockScroll } from "@birdwatch/ui";
import { addDays, subMilliseconds } from "date-fns";
import { useState } from "react";
import { Button } from "@/components/ui/button/button";
import { Calendar } from "@/components/ui/calendar/calendar";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog/dialog";
import useTimezone from "@/hooks/use-timezone";
import { formatDate } from "@/libs/date-fns";

export type DateRangeChange = {
  start?: string;
  end?: string;
  label: string;
};

/**
 * Which bound a single-date pick writes. `start` is the day's first instant
 * (lower-bound operators); `end` is its last (upper-bound operators), so
 * "on or before Mar 3" includes everything that happened on Mar 3.
 */
export type DateBound = "start" | "end";

type FilterCustomDatePickerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (date: DateRangeChange) => void;
  value?: {
    start?: string;
    end?: string;
  };
  /** `single` picks one day and writes only `bound`; defaults to a two-day range. */
  mode?: "range" | "single";
  bound?: DateBound;
};

/** Last instant of the picked day — the inclusive upper bound. */
function endOfDay(date: Date): Date {
  return new Date(subMilliseconds(addDays(date, 1), 1).toISOString());
}

export const FilterCustomDatePicker = ({
  open,
  onOpenChange,
  onChange,
  value,
  mode = "range",
  bound = "start",
}: FilterCustomDatePickerProps) => {
  const { timeZone } = useTimezone();
  const initial =
    mode === "single" && bound === "end" ? value?.end : value?.start;
  const [fromDate, setFromDate] = useState<Date | undefined>(
    initial ? new Date(initial) : undefined,
  );
  const [toDate, setToDate] = useState<Date | undefined>(
    value?.end ? new Date(value.end) : undefined,
  );

  const onSaveSingle = (picked: Date) => {
    const at =
      bound === "end" ? endOfDay(picked) : new Date(picked.toISOString());
    onChange({
      [bound]: at.toISOString(),
      label: formatDate(at.toISOString(), "MMM d", timeZone),
    } as DateRangeChange);
    onOpenChange(false);
  };

  const onSaveRange = (from: Date, to: Date) => {
    const start = new Date(from.toISOString());
    const end = endOfDay(to);
    onChange({
      start: start.toISOString(),
      end: end.toISOString(),
      label: `${formatDate(start.toISOString(), "MMM d", timeZone)} - ${formatDate(end.toISOString(), "MMM d", timeZone)}`,
    });
    onOpenChange(false);
  };

  const onSave = () => {
    if (mode === "single") {
      if (fromDate) onSaveSingle(fromDate);
      return;
    }
    if (fromDate && toDate) onSaveRange(fromDate, toDate);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col">
        <DialogHeader className="contents space-y-0 text-left">
          <DialogTitle>Pick a date...</DialogTitle>
          <DialogDescription className="sr-only">
            Pick a date...
          </DialogDescription>
        </DialogHeader>
        <BlockScroll>
          <div className="px-3 xl:px-6 pt-1 flex flex-col gap-6">
            <div className="flex items-center justify-center text-center gap-4">
              {mode === "single" ? (
                <Calendar
                  mode="single"
                  selected={fromDate}
                  defaultMonth={fromDate}
                  onSelect={(date) => setFromDate(date ?? undefined)}
                  numberOfMonths={1}
                  timeZone={timeZone}
                  showOutsideDays={false}
                />
              ) : (
                <Calendar
                  mode="range"
                  selected={{ from: fromDate, to: toDate }}
                  defaultMonth={fromDate}
                  onSelect={(date) => {
                    setFromDate(date?.from);
                    setToDate(date?.to);
                  }}
                  numberOfMonths={2}
                  timeZone={timeZone}
                  monthClassName="mr-2 last:mr-0"
                  showOutsideDays={false}
                />
              )}
            </div>
          </div>
        </BlockScroll>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" kind="outline" variant="neutral">
              Cancel
            </Button>
          </DialogClose>
          <Button kind="solid" variant="primary" onClick={onSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
